import React, { useMemo, useState } from "react";
import { Button, Modal } from "react-bootstrap";
import Select, { components as selectComponents } from "react-select";
import type { MultiValue, OptionProps } from "react-select";
import Loader from "../components/Loader";
import { formatCoupleOptionLabel } from "../utils/coupleLabels";

interface SelectOption {
  value: string | number;
  label: string;
}

interface SendFileModalProps {
  show?: boolean;
  handleClose: () => void;
  onSend: (payload: { recipientTypes: string[]; recipientIds: Array<string | number> }) => void;
  loader?: boolean;
  retreatRoastData?: Array<Record<string, any>>;
  fileType?: string;
}

function SendFileModal({
  show,
  handleClose,
  onSend,
  loader,
  retreatRoastData = [],
  fileType = "directory",
}: SendFileModalProps) {
  const [selectedRetreatants, setSelectedRetreatants] = useState<SelectOption[]>([]);
  const [selectedTeam, setSelectedTeam] = useState<SelectOption[]>([]);

  /* ---------- Checkbox option inside dropdown ---------- */
  const CheckboxOption = (props: OptionProps<SelectOption, true>) => (
    <selectComponents.Option {...props}>
      <input
        type="checkbox"
        checked={props.isSelected}
        readOnly
        style={{ marginRight: "8px" }}
      />
      <label>{props.label}</label>
    </selectComponents.Option>
  );

  /* ---------- Options (Alphabetically Sorted) ---------- */
  const retreatantOptions = useMemo(() => {
    return retreatRoastData
      .filter((item) => item.attendeeType === "Retreatant")
      .map((item) => ({
        value: item.id,
        label: formatCoupleOptionLabel(item),
      }))
      .sort((a, b) => a.label.localeCompare(b.label)); // Alphabetical sort
  }, [retreatRoastData]);

  const teamOptions = useMemo(() => {
    return retreatRoastData
      .filter((item) => item.attendeeType === "Team")
      .map((item) => ({
        value: item.id,
        label: formatCoupleOptionLabel(item),
      }))
      .sort((a, b) => a.label.localeCompare(b.label)); // Alphabetical sort
  }, [retreatRoastData]);

  /* ---------- Check if all options are selected ---------- */
  const isAllRetreataantsSelected =
    retreatantOptions.length > 0 &&
    selectedRetreatants.length === retreatantOptions.length;

  const isAllTeamSelected =
    teamOptions.length > 0 && selectedTeam.length === teamOptions.length;

  /* ---------- Handlers ---------- */
  const toggleSelectAllRetreatants = () => {
    if (isAllRetreataantsSelected) {
      setSelectedRetreatants([]);
    } else {
      setSelectedRetreatants(retreatantOptions);
    }
  };

  const toggleSelectAllTeam = () => {
    if (isAllTeamSelected) {
      setSelectedTeam([]);
    } else {
      setSelectedTeam(teamOptions);
    }
  };

  const handleConfirm = () => {
    if (selectedRetreatants.length === 0 && selectedTeam.length === 0) {
      alert("Please select at least one recipient");
      return;
    }

    const recipientTypes = [];
    const recipientIds = [];

    if (selectedRetreatants.length > 0) {
      recipientTypes.push("Retreatant");
      recipientIds.push(...selectedRetreatants.map((r) => r.value));
    }

    if (selectedTeam.length > 0) {
      recipientTypes.push("Team");
      recipientIds.push(...selectedTeam.map((t) => t.value));
    }

    onSend({
      recipientTypes,
      recipientIds,
    });

    resetState();
  };

  const resetState = () => {
    setSelectedRetreatants([]);
    setSelectedTeam([]);
  };

  const handleModalClose = () => {
    handleClose();
    resetState();
  };

  /* ---------- Render ---------- */
  return (
    <Modal show={show} onHide={handleModalClose} size="lg" centered>
      <Modal.Header closeButton>
        <Modal.Title>
          {fileType === "program" ? "Send Program" : "Send Directory"}
        </Modal.Title>
      </Modal.Header>

      {loader ? (
        <div style={{ margin: "50px", textAlign: "center" }}>
          <Loader />
        </div>
      ) : (
        <>
          <Modal.Body>
            <div style={{ fontWeight: 600, marginBottom: "16px" }}>
              Select couples for this{" "}
              {fileType === "program" ? "program" : "directory"}:
            </div>

            {/* ---------- Two Column Layout with Divider ---------- */}
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "1fr 1px 1fr",
                gap: "24px",
                alignItems: "stretch",
              }}
            >
              {/* Retreatant Column */}
              <div>
                <div style={{ fontWeight: 600, marginBottom: "8px" }}>
                  Retreatant
                </div>

                <label style={checkboxStyle(isAllRetreataantsSelected)}>
                  <input
                    type="checkbox"
                    checked={isAllRetreataantsSelected}
                    onChange={toggleSelectAllRetreatants}
                    style={{ marginRight: "8px" }}
                  />
                  Select All Retreatants
                </label>

                <Select
                  isMulti
                  closeMenuOnSelect={false}
                  hideSelectedOptions={false}
                  options={retreatantOptions}
                  components={{ Option: CheckboxOption }}
                  value={selectedRetreatants}
                  onChange={(vals: MultiValue<SelectOption>) => setSelectedRetreatants([...(vals || [])])}
                  placeholder="Select Retreatants"
                  menuPortalTarget={document.body}
                  styles={{
                    menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                  }}
                />
              </div>

              {/* Vertical Divider */}
              <div
                style={{
                  width: "1px",
                  backgroundColor: "#dee2e6",
                }}
              />

              {/* Team Column */}
              <div>
                <div style={{ fontWeight: 600, marginBottom: "8px" }}>Team</div>

                <label style={checkboxStyle(isAllTeamSelected)}>
                  <input
                    type="checkbox"
                    checked={isAllTeamSelected}
                    onChange={toggleSelectAllTeam}
                    style={{ marginRight: "8px" }}
                  />
                  Select All Team
                </label>

                <Select
                  isMulti
                  closeMenuOnSelect={false}
                  hideSelectedOptions={false}
                  options={teamOptions}
                  components={{ Option: CheckboxOption }}
                  value={selectedTeam}
                  onChange={(vals: MultiValue<SelectOption>) => setSelectedTeam([...(vals || [])])}
                  placeholder="Select Team"
                  menuPortalTarget={document.body}
                  styles={{
                    menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                  }}
                />
              </div>
            </div>

            <small style={{ display: "block", marginTop: "12px" }}>
              Select specific couples from the dropdown, or check "Select All"
              to choose everyone in that category.
            </small>
          </Modal.Body>

          <Modal.Footer>
            <Button variant="secondary" onClick={handleModalClose}>
              Cancel
            </Button>
            <Button variant="primary" onClick={handleConfirm}>
              Send
            </Button>
          </Modal.Footer>
        </>
      )}
    </Modal>
  );
}

/* ---------- Styles ---------- */
const checkboxStyle = (active: boolean): React.CSSProperties => ({
  display: "flex",
  alignItems: "center",
  padding: "8px 10px",
  marginBottom: "10px",
  border: active ? "2px solid #0d6efd" : "1px solid #dee2e6",
  borderRadius: "4px",
  backgroundColor: active ? "#e7f1ff" : "transparent",
  cursor: "pointer",
});

export default SendFileModal;
